import { RootState } from '@/store'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { addManualInputThunk, changeManualInputsThunk, deleteManualInputThunk, loadSimThunk, reRunSimSeqThunk, runSimSeqThunk, runSimThunk, updateTopologyThunk, updateUnitTTPSThunk } from './actions'
import { FlowsheetContent, LineRecord, NodeRecord, SetOperation, SheetContent, Spreadsheet } from '@/components/FlowSheetEditor/services/modal'
import { getEmptyFlowSheetContent } from '@/components/FlowSheetEditor/services/storage'
import { Cell } from '@antv/x6'
import assign from 'lodash/assign'
import { SimulationModal } from '@/services/project/simulation.service'
import { SvgStreamParams } from './types'
import { EquipmentType } from '@/types/common'

export enum PaperSize {
  A4 = 'A4',
  A3 = 'A3'
}

export enum PaperDirection {
  Portrait,
  Landscape,
}

export enum TempLineType {
  None,
  Solid,
  Dashed
}

export interface FlowTemplate {
  size?: PaperSize,
  direction?: PaperDirection,
  border?: TempLineType
}

export interface UnitSimOperation {
  uuid: string
  status: number
  type: EquipmentType
  message: string
  name: string
  solverStatus: number
}
export interface ITopologyState {
  id: number
  operations: UnitSimOperation[],
  message: string
  status: number
  tpps: string[]
}

export interface IFlowSheet {
  topologyState: ITopologyState | null,
  setOperations: SetOperation[]
  spreadsheets: Spreadsheet[],
  template?: FlowTemplate
  status: RunState
  sheetId: string;
  content: FlowsheetContent
  simulation: SimulationModal | null
}

export interface ISheetData {
  image?: string
  nodes: NodeRecord[]
  lines: LineRecord[]
  connections: Cell.Properties[]
  polylines: Cell.Properties[]
  jumps: Cell.Properties[]
  overridedNodes?: Cell.Properties[]
}

export enum RunState {
  Init = 0,
  Stop = 1,
  Run = 2,
}

const emptyContent = getEmptyFlowSheetContent();

const initialState: IFlowSheet = {
  topologyState: null,
  setOperations: [],
  spreadsheets: [],
  status: RunState.Init,
  sheetId: emptyContent.sheets[0].id,
  content: emptyContent,
  simulation: null
}

export const flowsheetSlice = createSlice({
  name: 'flowsheet',
  initialState,
  reducers: {
    removeConnectionFromSheet: (state,  action: PayloadAction<{
      sheetId: string,
      id: string,
      line?: {
        id: string,
        changes: {[key: string]: any}
      }
    }>) => {
      const {sheetId, id, line} = action.payload
      const sheet = state.content.sheets.find((item) => item.id === sheetId)
      if (sheet) {
        sheet.connections = sheet.connections.filter(item => item.id !== id)
        if (line) {
          const targetLine = sheet.lines.find(item => item.id === line.id)
          if (targetLine) {
            assign(targetLine, line.changes)
          }
        }
      }
    },
    addConnectionToSheet: (state, action: PayloadAction<SvgStreamParams>) => {
      const {sheetId, connection} = action.payload
      const sheet = state.content.sheets.find((item) => item.id === sheetId)
      if (sheet) {
        sheet.connections.push(connection)
      }
    },
    setFlowSimulation: (state, action: PayloadAction<SimulationModal>) => {
      state.simulation = action.payload
    },
    setFSTempContent: (state, action: PayloadAction<Cell.Properties[]>) => {
      state.content.tempCells = action.payload
    },

    saveCurrentSheet: (state, action: PayloadAction<ISheetData>) => {
      const currentSheet = state.content.sheets.find(item => item.id === state.sheetId)
      if (!currentSheet) {
        console.error('not found the sheet of ', state.sheetId)
        return
      }
      assign(currentSheet, action.payload)
    },
    deleteSheet: (state, action: PayloadAction<string>) => {
      state.content.sheets = state.content.sheets.filter(item => item.id !== action.payload)
      state.sheetId = state.content.sheets[0].id
    },
    setSheetId: (state, action: PayloadAction<string>) => {
      state.sheetId = action.payload
    },
    setFSContent: (state, action: PayloadAction<FlowsheetContent>) => {
      state.content = action.payload;
      state.sheetId = action.payload.sheets[0].id
    },
    addSheet: (state, action: PayloadAction<SheetContent>) => {
      const sheets = state.content.sheets;
      sheets.push(action.payload)
    },
    updateCurrentSheet: (state, action: PayloadAction<Partial<SheetContent>>) => {
      const currentSheet = state.content.sheets.find(item => item.id === state.sheetId)
      if (!currentSheet) {
        console.error('not found the sheet of ', state.sheetId)
        return
      }
      assign(currentSheet, action.payload)
    },
    setSimStatus: (state, action: PayloadAction<RunState>) => {
      state.status = action.payload
    },
    setTopologyState: (state, action: PayloadAction<any>) => {
      state.topologyState = action.payload
    },
    setSetOperations: (state, action: PayloadAction<SetOperation[]>) => {
      state.setOperations = action.payload || []
    },
    addSetOperation: (state, action: PayloadAction<SetOperation>) => {
      state.setOperations = [...state.setOperations, action.payload]
    },
    removeSetOperation: (state, action: PayloadAction<string>) => {
      state.setOperations = state.setOperations.filter(item => item.id !== action.payload)
    },
    updateSetOperation: (state, action: PayloadAction<SetOperation>) => {
      const data = state.setOperations;
      const index = data.findIndex(item => item.id === action.payload.id)
      if (index > -1) {
        data[index] = action.payload
      }
    },
    setSpreadsheets: (state, action: PayloadAction<Spreadsheet[]>) => {
      state.spreadsheets = action.payload
    },
    addSpreadsheet: (state, action: PayloadAction<Spreadsheet>) => {
      state.spreadsheets = [...state.spreadsheets, action.payload]
    },
    updateSpreadsheet: (state, action: PayloadAction<Spreadsheet>) => {
      const index = state.spreadsheets.findIndex(item => item.id === action.payload.id)
      const data = state.spreadsheets;
      if (index > -1) {
        data[index] = action.payload
      }
    },
    removeSpreadsheet: (state, action: PayloadAction<string>) => {
      state.spreadsheets = state.spreadsheets.filter(item => item.id !== action.payload)
    },
    updateTemplate: (state, action: PayloadAction<Partial<FlowTemplate>>) => {
      state.template = {...(state.template || {}), ...action.payload}
    },
    updateSheetImages: (state, action: PayloadAction<string[]>) => {
      const images = action.payload
      state.content.sheets.map((item, index) => {
        item.image = images[index] || item.image
        return item
      })
    }
  },
  extraReducers: (builder) => {
    [
      runSimSeqThunk.fulfilled,
      reRunSimSeqThunk.fulfilled,
      loadSimThunk.fulfilled,
      runSimThunk.fulfilled,
      updateTopologyThunk.fulfilled,
      addManualInputThunk.fulfilled,
      deleteManualInputThunk.fulfilled,
      changeManualInputsThunk.fulfilled,
      updateUnitTTPSThunk.fulfilled,
    ].forEach((fulfilledAction) => {
      builder.addCase(fulfilledAction, (state, action) => {
        console.log('topologyState', action.payload)
        state.topologyState = action.payload as any
      })
    });
  }
})

export const {
  setTopologyState, setSetOperations, addSetOperation, removeSetOperation, updateSetOperation,
  addSpreadsheet,
  updateSpreadsheet,
  removeSpreadsheet,
  setSpreadsheets,
  updateTemplate,
  setSimStatus,
  setFSContent,
  setSheetId,
  deleteSheet,
  addSheet,
  saveCurrentSheet,
  setFSTempContent,
  updateCurrentSheet,
  setFlowSimulation,
  addConnectionToSheet,
  removeConnectionFromSheet,
  updateSheetImages
} = flowsheetSlice.actions

export const getTopologyState = (state: RootState) => state.flowsheet.topologyState

export const getSetOperations = (state: RootState) => state.flowsheet.setOperations

export const getSpreadsheets = (state: RootState) => state.flowsheet.spreadsheets

export const getFlowTemplate = (state: RootState) => state.flowsheet.template

export const getStatus = (state: RootState) => state.flowsheet.status
export const getSheetsSelector = (state: RootState) => state.flowsheet.content.sheets;
export const getFSContentSelector = (state: RootState) => state.flowsheet.content;
export const getCurrentSheetIdSelector = (state: RootState) => state.flowsheet.sheetId;
export const getFlowSimulationSelector = (state: RootState) => state.flowsheet.simulation;

interface UnitsStatusGroup {
  errors:  UnitSimOperation[],
  warnings: UnitSimOperation[],
  succeds: UnitSimOperation[]
}
export function groupUnitsByStatus(operations: UnitSimOperation[]): UnitsStatusGroup {
  return operations.reduce((res, cur) => {
    const status = cur.status;
    if (status >= 500) {
      res.errors.push(cur)
    } else if (status <= 400 && status > 0) {
      res.warnings.push(cur)
    } else {
      res.succeds.push(cur)
    }
    return res;
  }, {
    errors: [],
    warnings: [],
    succeds: []
  } as UnitsStatusGroup)
}